import React, { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import useTitle from "../../hooks/useTitle"
import { addIcon, userProfileIcon2 } from "../../assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import {
  UserGroupIcon,
  BuildingOffice2Icon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  Square3Stack3DIcon,
  ListBulletIcon,
  SwatchIcon,
  InformationCircleIcon,
  ClockIcon,
  ArrowLeftCircleIcon,
  WrenchIcon,
  RocketLaunchIcon,
  DocumentTextIcon,
  CursorArrowRippleIcon,
  SunIcon,
} from "@heroicons/react/24/solid"
import EtContentEditorLight2 from "../../utils/EtContentEditorLight2"
// import EtBtnFormBasic from "../../components/common/buttons/EtBtnFormBasic"
import { getTimeSince } from "../../utils/EtGetTimeSince"
import { formatDate, formatDateTime } from "../../utils/formatDate"
import PulseLoader from "react-spinners/PulseLoader"
import {
  useGetSponsorOrgTrailSessionPadsQuery,
  useUpdateSponsorOrgTrailSessionPadMutation,
} from "./sponsorOrgTrailSessionPadsApiSlice"
import { useGetSponsorUserOrgTrailSessionActionsQuery } from "../sponsorUserOrgTrailSessionActions/sponsorUserOrgTrailSessionActionsApiSlice" // 01 Sep 2024
import { useGetUserPicQuery } from "../users/usersApiSlice"
import EtDocSelfNoteAllUsersIconSVGComp from "@/assets/img/icons/svgComp/EtDocSelfNoteAllUsersIconSVGComp"
import EtDocSelfUserNoteIconSVGComp from "@/assets/img/icons/svgComp/EtDocSelfUserNoteIconSVGComp"
import EtDocSharedNoteAllUsersIconSVGComp from "@/assets/img/icons/svgComp/EtDocSharedNoteAllUsersIconSVGComp"
import EtDocSharedUserNoteIconSVGComp from "@/assets/img/icons/svgComp/EtDocSharedUserNoteIconSVGComp"
// 26 Aug 2024
import EtSponsorEvalIcon2SVGComp from "@/assets/img/icons/svgComp/EtSponsorEvalIcon2SVGComp"
// import EtModalCreateUserChallengeSponsorEval from "@/components/common/modal/EtModalCreateUserChallengeSponsorEval"
import EtModalAddOrUpdateUserChallengeSponsorEval from "@/components/common/modal/EtModalAddOrUpdateUserChallengeSponsorEval"
// 11 Sep 2024
import EtModalAddOrUpdateSponsorUserOrgTrailSessionAction from "@/components/common/modal/EtModalAddOrUpdateSponsorUserOrgTrailSessionAction"
import EtSponsorUserActionIcon1SVGComp from "@/assets/img/icons/svgComp/EtSponsorUserActionIcon1SVGComp"
import EtUserActionListByIdComp from "../userActions/EtUserActionListByIdComp" // 02 Sep 2024 - to show user action progress

import EtUserSessionQNABoard from "../userSessionQNAResponses/EtUserSessionQNABoard" // 28 May 2026

import { useGetOrgTrailSessionCohortsQuery } from "../orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice" // 22 Jun 2026

import {
  useGetUserTrailSessionPlansQuery,
} from "../userTrailSessions/userTrailSessionPlansApiSlice" // 22 Jun 2026

// 03 Aug 2026
import EtSponsorSessionChallengeEvaluations from "./EtSponsorSessionChallengeEvaluations"

const EtSponsorOrgTrailSessionPadCompProd = () => {
  // First Created - 07 Sep 2024
  // Author - Atul Pandey
  // Inherited from - EtCoachOrgTrailSessionPad
  // Updated - 08 Sep 2024 - moved sponsorId to useParams
  // Updated - 10 Sep 2024 - Added filter to the session user to exclude sponsor from the user list
  // Updated - 11 Sep 2024 - Added Add or Update Session Planned User Actions modal
  // Updated - 12 Sep 2024 - Changed orgId to sponsorOrgId in update and delete mutations for all users notes, self notes, self user notes and shared user notes
  // Updated - 23 Feb 2025 - Copied and Renamed for prod deployment
  // Updated - 18 May 2025 - changed to orgName to sponsorOrgName in the Sponsor Header section in JSX
  // Updated - 28 Oct 2025 - removed the filtering out sponsor from the list of users - the sponsor can also be a user
  // Updated - 31 Jul 2026 - refined for cohortId
  // Updated - 02 Aug 2026 - refined for multiple session challenge evals
  // Updated - 03 Aug 2026 - componentized multiple session challenge evals


  // 1 - Set Page Title
  useTitle("Sponsor Org Trail Session Pad")

  // 2 - Extract params from the URL
  const { sponsorId, orgId, trailId, sessionId } = useParams()
  // console.log('orgId', orgId)
  // console.log('trailId', trailId)
  // console.log('sessionId', sessionId)

  // 3 - RTK queries - extract and update data mutation
  const {
    data: sponsorOrgTrailSessionPadsData,
    isLoading,
    isError,
    error,
    refetch, // 27 Aug 2024 -Add refetch to trigger a data refresh
  } = useGetSponsorOrgTrailSessionPadsQuery(
    {
      sponsorId,
      sponsorOrgId: orgId,
      userOrgId: orgId,
      trailId,
      sessionId,
      active: true,
    },
    {
      skip: !sponsorId || !orgId || !trailId || !sessionId,
    }
  )
  // console.log('sponsor org trail session data:', sponsorOrgTrailSessionPadsData)

  // 31 Jul 2026
  const {
    data: userTrailSessionPlansData,
    isLoading: isSessionPlansLoading,
    isError: isSessionPlansError,
    error: sessionPlansError,
  } = useGetUserTrailSessionPlansQuery(
    {
      trailId,
      sessionId,
      active: true,
    },
    {
      skip: !trailId || !sessionId,
    }
  )

  const userTrailSessionPlans =
  userTrailSessionPlansData?.ids
    ?.map((id) => userTrailSessionPlansData.entities[id])
    ?.filter((plan) => plan?.active !== false) || []

  // 4a - Extract data from entity adapters
  const sponsorOrgTrailSessionPads = sponsorOrgTrailSessionPadsData?.ids.map(
    (id) => {
      const sponsorOrgTrailSessionPad = sponsorOrgTrailSessionPadsData?.entities[id]
      return sponsorOrgTrailSessionPad
    }
  )
  // console.log('sponsorOrgTrailSessionPads:', sponsorOrgTrailSessionPads)

  // 4b - Filter Org trail sessions for the selected org trail session
  const filteredSponsorOrgTrailSessionPad = sponsorOrgTrailSessionPads?.find(
    (pad) =>
      pad?.sponsorId?._id === sponsorId &&
      pad?.sponsorOrgId?._id === orgId &&
      pad?.userOrgId?._id === orgId &&        // 31 Jul 2026
      pad?.trailId?._id === trailId &&
      pad?.sessionId?._id === sessionId
  )

  // console.log("Filtered Sponsor Org Trail Session Pad:",filteredSponsorOrgTrailSessionPad)

  // 5 - Local state to hold form data
  const [formData, setFormData] = useState({
    sponsorSessionSelfNotes: "",
    sponsorSessionAllUsersNotes: "",
    sessionUsers: [],
    // Add other fields as needed
  })

  // 6 - Handler for form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // 31 Jul 2026
  if (isLoading || isSessionPlansLoading) {
    return <PulseLoader color={"#ea580c"} />
  }

  if (isError) {
    return <div>Error occurred: {error?.data?.message}</div>
  }

  if (isSessionPlansError) {
    return <div>Error occurred: {sessionPlansError?.data?.message}</div>
  }

  // if (isLoading) return <PulseLoader color={"#ea580c"} />
  // if (isError) return <div>Error occurred: {error?.data?.message}</div>

  return (
    <div className="m-1 p-1 bg-gray-200 rounded">
      <EtSponsorOrgTrailSessionPadForm
        padData={filteredSponsorOrgTrailSessionPad}
        refetch={refetch}
        userTrailSessionPlans={userTrailSessionPlans} // 31 Jul 2026
      />
    </div>
  )
}

const EtSponsorOrgTrailSessionPadForm = ({ 
  padData, 
  refetch,
  userTrailSessionPlans = [], // 31 Jul 2026
 }) => {
  // First Created - 09 Aug 2024
  // Author - Atul Pandey
  // Updated - 10 Aug 2024 - Added Sponsor Session Note mutation and form components
  // Updated - 25 Aug 2024 - Added Sponsor Session Challenge Evaluation for a specific org trail session
  // Updated - 26 Aug 2024 - Added Sponsor evaluation section along with modal to create new evaluations
  // Updated - 27 Aug 2024 - Refcactored display to include Challenge Instructions and Dimension Headers
  // Updated - 01 Sep 2024 - Added Sponsor Session Planned Actions for all users in a session
  // Updated - 02 Sep 2024 - Added the sponsor session planned action creation modal
  // Updated - 02 Sep 2024 - Added the sponsor session
  // Updated - 10 Sep 2024 - Added filter to the session user to exclude sponsor from the user list
  // Updated - 11 Sep 2024 - Added Add or Update Session Planned User Actions modal
  // Updated - 12 Sep 2024 - Changed orgId to sponsorOrgId in update and delete mutations for all users notes, self notes, self user notes and shared user notes
  // Updated - 13 Sep 2024 - Refined display of Coach's notes based on the note length
  // Updated - 13 Sep 2024 - Added state variables to toggle show / hide sponsor all users note details
  // Updated - 13 Sep 2024 - Added state variables to toggle show / hide sponsor self note (all users) details
  // Updated - 13 Sep 2024 - Added state variables to toggle show / hide sponsor shared note details for specific user
  // Updated - 13 Sep 2024 - Added state variables to toggle show / hide sponsor self note details for specific user
  // Updated - 18 May 2025 - changed to orgName to sponsorOrgName in the Sponsor Header section in JSX
  // Updated - 28 Oct 2025 - removed the filtering out sponsor from the list of users - the sponsor can also be a user
  // Updated - 31 Jul 2026 - refined for cohortId
  // Updated - 02 Aug 2026 - refined for multiple session challenge evals
  // Updated - 03 Aug 2026 - componentized multiple session challenge evals

  // console.log('padData', padData)
  // console.log('userTrailSessionPlans', userTrailSessionPlans)

  // 1a - state variables for show / hide
  const [showDetails, setShowDetails] = useState(true)
  const [showSessionInfo, setShowSessionInfo] = useState(false)
  const [showSessionUsers, setShowSessionUsers] = useState(false)
  const [showSelfNotes, setShowSelfNotes] = useState(false) // Sponsor's self notes - for all users
  const [showAllUsersNotes, setShowAllUsersNotes] = useState(false) // Sponsor's shared notes - for all users
  const [showSelfUserNotes, setShowSelfUserNotes] = useState({}) // Sponsor's self notes - for specific users - 11 Aug 2024 changed to object from boolean
  const [showSharedUserNotes, setShowSharedUserNotes] = useState({}) // Sponsor's shared notes - for specific users - 11 Aug 2024 changed to object from boolean
  const [selfEditorKey, setSelfEditorKey] = useState("selfNote-" + Date.now()) // to address multiple editors issue
  const [allUsersEditorKey, setAllUsersEditorKey] = useState(
    "allUsersNote-" + Date.now()
  ) // to address multiple editors issue
  const [selfUserEditorKey, setSelfUserEditorKey] = useState(
    "selfUserNote-" + Date.now()
  ) // 11 Aug 2024 - to address multiple editors issue
  const [sharedUserEditorKey, setSharedUserEditorKey] = useState(
    "sharedUserNote-" + Date.now()
  ) // 11 Aug 2024 - to address multiple editors issue
  const [showUserSections, setShowUserSections] = useState(false) // 11 Aug 2024 - For Section 5 - Specific User sections
  const [showUserSectionDetails, setShowUserSectionDetails] = useState({}) // 11 Aug 2024 - For Section 5 - Specific User sections
  const [showSharedUserSections, setShowSharedUserSections] = useState(false) // 11 Aug 2024 - For Section 5 - Specific SharedUser sections
  const [showSharedUserSectionDetails, setShowSharedUserSectionDetails] =
    useState({}) // 11 Aug 2024 - For Section 5 - Specific SharedUser sections
  const [showSponsorEvalsSection, setShowSponsorEvalsSection] = useState(false) // 26 Aug 2024 - For Sponsor's Evaluation
  const [showChallengeSections, setShowChallengeSections] = useState({}) // 26 Aug 2024
  const [showChallengeHeadDetails, setShowChallengeHeadDetails] = useState({}) // 26 Aug 2024
  const [showChallengeHeadSectionDetails, setShowChallengeHeadSectionDetails] =
    useState({}) // 27 Aug 2024
  const [showChallengeDimDetails, setShowChallengeDimDetails] = useState({}) // 26 Aug 2024
  const [selectedUserId, setSelectedUserId] = useState(null) // 26 Aug 2024
  const [selectedUserOrgId, setSelectedUserOrgId] = useState(null) // 26 Aug 2024
  const [selectedChallengeId, setSelectedChallengeId] = useState(null) // 26 Aug 2024
  const [existingEvalData, setExistingEvalData] = useState(null) // 27 Aug 2024 - to allow create / update of sponsor user challenge evals
  const [mode, setMode] = useState("create") // 27 Aug 2024 - to allow create / update of sponsor user challenge evals
  const [actionMode, setActionMode] = useState("create") // 10 Sep 2024 - to allow create / update of sponsor user session planned actions
  const [showInstructionDetails, setShowInstructionDetails] = useState({}) // 19 Aug 2024
  const [existingActionData, setExistingActionData] = useState(null) // 10 Sep 2024 - to pass existing data to the modal

  const [showSponsorAllUsersActionsSection, setShowSponsorAllUsersActionsSection ] = useState(false) // 01 Sep 2024 - For Section 8
  const [showSponsorAllUsersCreatedActions, setShowSponsorAllUsersCreatedActions] = useState(false) // 02 Sep 2024 - Section 8
  const [showSponsorUserActionUpdate, setShowSponsorUserActionUpdate] = useState({}) // 02 Sep 2024 - for showing user action updates for sponsor planned user actions

  const [showSponsorSelfAllUsersNoteDetail, setShowSponsorSelfAllUsersNoteDetail] = useState({}) // 13 Sep 2024 - for showing sponsor self note (for all users) detail when it is >100 chars
  const [showSponsorAllUsersNoteDetail, setShowSponsorAllUsersNoteDetail] = useState({}) // 13 Sep 2024 - for showing sponsor note detail when it is >100 chars
  const [showSponsorSelfUserNoteDetail, setShowSponsorSelfUserNoteDetail] = useState({}) // 13 Sep 2024 - for showing sponsor self note detail for a specific user when it is >100 chars
  const [showSponsorSharedUserNoteDetail, setShowSponsorSharedUserNoteDetail] = useState({}) // 13 Sep 2024 - for showing sponsor shared note detail for a specific user when it is >100 chars

  // 1b - State for Modal -- 26 Aug 2024
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [triggeringUrl, setTriggeringUrl] = useState(null) // 26 Aug 2024

  const [isActionModalOpen, setIsActionModalOpen] = useState(false) // 02 Sep 2024 for creating Sponsor Planned Session Action Modal
  const [triggeringActionUrl, setTriggeringActionUrl] = useState(null) // 02 Sep 2024 for creating Sponsor Planned Session Action Modal

  const [selectedCohortId, setSelectedCohortId] = useState(null) // 31 Jul 2026

  // 02 Aug 2026
  const [
    selectedEvaluationNumberByChallenge,
    setSelectedEvaluationNumberByChallenge,
  ] = useState({})

  // 1c - Get sponsor's user planned actions data - 01 Sep 2024
  const {data: sponsorUserOrgTrailSessionActionsData, refetch: refetchActions} = useGetSponsorUserOrgTrailSessionActionsQuery()
  const sponsorSessionPlannedActions = sponsorUserOrgTrailSessionActionsData?.ids?.map((id) => {
    const sponsorSessionPlannedAction = sponsorUserOrgTrailSessionActionsData?.entities[id]
    return sponsorSessionPlannedAction
  })
  // console.log('Sponsor Session Planned Actions:', sponsorSessionPlannedActions)

  // 1d - Load state from localStorage -- 11 Aug 2024 - repurposed from EtUserTrailSessionsList - 12 Jun 2024
  // useEffect(() => {
  //     const storedShowDetails = localStorage.getItem('showDetails');
  //     const storedShowSelfNotes = localStorage.getItem('showSelfNotes');
  //     const storedShowAllUsersNotes = localStorage.getItem('showAllUsersNotes');
  //     const storedShowSelfUserNotes = localStorage.getItem('showSelfUserNotes');

  //     if (storedShowDetails && !showDetails) {
  //         setShowDetails(JSON.parse(storedShowDetails));
  //     }
  //     if (storedShowSelfNotes && !showSelfNotes) {
  //         setShowSelfNotes(JSON.parse(storedShowSelfNotes));
  //     }
  //     if (storedShowAllUsersNotes && !showAllUsersNotes) {
  //         setShowAllUsersNotes(JSON.parse(storedShowAllUsersNotes));
  //     }
  //     if (storedShowSelfUserNotes) {
  //         setShowSelfUserNotes(JSON.parse(storedShowSelfUserNotes));
  //     } else {
  //         setShowSelfUserNotes({}); // Initialize as an empty object if not present
  //     }
  // }, [ ]);

  // 1c-  Save state to localStorage before page reload -- 11 Aug 2024 - repurposed from EtUserTrailSessionsList - 12 Jun 2024
  // useEffect(() => {
  //     const handleBeforeUnload = () => {
  //         localStorage.setItem('showDetails', JSON.stringify(showDetails));
  //         localStorage.setItem('showSelfNotes', JSON.stringify(showSelfNotes));
  //         localStorage.setItem('showAllUsersNotes', JSON.stringify(showAllUsersNotes));
  //         localStorage.setItem('showSelfUserNotes', JSON.stringify(showSelfUserNotes));
  //     };

  //     window.addEventListener('beforeunload', handleBeforeUnload);
  //     return () => {
  //         window.removeEventListener('beforeunload', handleBeforeUnload);
  //     };
  // }, [showDetails,showSelfNotes, showAllUsersNotes, showSelfUserNotes]);
  // useEffect(() => {
  //     const handleBeforeUnload = () => {
  //         localStorage.setItem('showDetails', JSON.stringify(showDetails));
  //         localStorage.setItem('showSelfNotes', JSON.stringify(showSelfNotes));
  //         localStorage.setItem('showAllUsersNotes', JSON.stringify(showAllUsersNotes));
  //         localStorage.setItem('showSelfUserNotes', JSON.stringify(showSelfUserNotes));
  //     };

  //     window.addEventListener('beforeunload', handleBeforeUnload);
  //     return () => {
  //         window.removeEventListener('beforeunload', handleBeforeUnload);
  //     };
  // }, [showDetails, showSelfNotes, showAllUsersNotes, showSelfUserNotes]);

  // 2a - Basic Pad Data
  const sponsorId = padData?.sponsorId?._id
  const sponsorOrgId = padData?.sponsorId?.userOrgId
  const sponsorOrgName = padData?.sponsorOrgId?.organizationName // 08 Sep 2024
  const username = padData?.sponsorId?.username
  const name = padData?.sponsorId?.name
  const sponsorOrgRole = padData?.sponsorId?.userOrgRole?.orgRoleName     // 08 Sep 2024
  const sponsorOrgTitle = padData?.sponsorId?.userOrgTitle?.orgTitleName  // 08 Sep 2024
  // const orgId = padData?.userOrgId
  const orgId = padData?.userOrgId?._id || padData?.userOrgId // 31 Jul 2026
  const trailId = padData?.trailId?._id
  const sessionId = padData?.sessionId?._id
  const orgName = padData?.orgId?.organizationName
  // const orgName = padData?.orgId?.organizationName // 18 May 2025
  const trailName = padData?.trailId?.trailName
  const trailPic = padData?.trailId?.pic
  const sessionName = padData?.sessionId?.sessionName
  const sessionPic = padData?.sessionId?.pic
  const sessionUsers = padData?.sessionUsers
  // const filteredSessionUsers = sessionUsers?.filter(user => user.userId._id !== sponsorId) // 10 Sep 2024 - filtering out sponsor from the list of users - the sponsor cannot be sponsor for themselves
  const filteredSessionUsers = sessionUsers // 28 Oct 2025 - removed the filtering out sponsor from the list of users - the sponsor can also be a user

  // 31 Jul 2026
  const userCohortIdByUserId = userTrailSessionPlans.reduce((acc, plan) => {
    const planTrailId = plan?.trailId?._id || plan?.trailId
    const planSessionId = plan?.sessionId?._id || plan?.sessionId
    const planUserId = plan?.userId?._id || plan?.userId
    const planCohortId = plan?.cohortId?._id || plan?.cohortId

    if (
      planTrailId === trailId &&
      planSessionId === sessionId &&
      planUserId &&
      planCohortId
    ) {
      acc[planUserId] = planCohortId
    }

    return acc
  }, {})

  // console.log('userCohortIdByUserId', userCohortIdByUserId)


  // const userCount = padData?.sessionUsers?.length
  const userCount = filteredSessionUsers?.length // 10 Sep 2024 - changed to filteredSessionUsers from sessionUsers
  const sessionDetails = padData?.sessionId
  const sponsorSessionAllUsersNotes = padData?.sponsorSessionAllUsersNotes // 10 Aug 2024
  const sponsorSessionSelfNotes = padData?.sponsorSessionSelfNotes // 10 Aug 2024
  const sponsorSessionSelfUserNotes = padData?.sponsorSessionSelfUserNotes // 11 Aug 2024
  const sponsorSessionSharedUserNotes = padData?.sponsorSessionSharedUserNotes // 11 Aug 2024
  const sponsorSessionEvals = padData?.sessionChallengeSponsorEvals // 26 Aug 2024
  const sessionChallenges = padData?.sessionId?.sessionChallenges // 26 Aug 2024
  

  // console.log('Session Users:', sessionUsers)
  // console.log('Filtered Session Users:', filteredSessionUsers)
  // console.log('Session Sponsor Evals:', sponsorSessionEvals)
  // console.log('orgId:', orgId)


  // 2b - Filter Sponsor Planned SessionActions - 01 Sep 2024
  const filteredSponsorUserPlannedActions = sponsorSessionPlannedActions?.filter(action => action.sponsorId._id === sponsorId && action.sponsorOrgId._id === sponsorOrgId && action.userOrgId._id === orgId)

  // console.log('Filtered Sponsor Planned Actions:', filteredSponsorUserPlannedActions)

  // 2c - Define and declare Navigation and state variables for Sponsor notes - 10 Aug 2024
  const navigate = useNavigate()
  const [sponsorSelfNotes, setSponsorSelfNotes] = useState(sponsorSessionSelfNotes)
  const [sponsorAllUsersNotes, setSponsorAllUsersNotes] = useState(
    sponsorSessionAllUsersNotes
  )
  const [sponsorSelfUserNotes, setSponsorSelfUserNotes] = useState(
    sponsorSessionSelfUserNotes
  ) // 11 Aug 2024
  const [sponsorSharedUserNotes, setSponsorSharedUserNotes] = useState(
    sponsorSessionSharedUserNotes
  ) // 11 Aug 2024
  const [sponsorEvals, setSponsorEvals] = useState(sponsorSessionEvals) // 26 Aug 2024

  // const [isEditing, setIsEditing] = useState(false)
  const [isSelfNoteEditing, setIsSelfNoteEditing] = useState(false)
  const [isAllUsersNoteEditing, setIsAllUsersNoteEditing] = useState(false)
  const [isSelfUserNoteEditing, setIsSelfUserNoteEditing] = useState({}) // 11 Aug 2024 - updated the state to object from boolean
  const [isSharedUserNoteEditing, setIsSharedUserNoteEditing] = useState({}) // 11 Aug 2024 - updated the state to object from boolean

  // 3a - Update mutation for adding, editing sponsor's notes and other activities - 10 Aug 2024
  const [updateSponsorOrgTrailSessionPad] =
    useUpdateSponsorOrgTrailSessionPadMutation()

  // 4a - Editor Value setting based on incoming data -- 10 Aug 2024 - 13 Jun 2024 (repurposed EtUserTrailSessionImplementation code)
  const [editorValue, setEditorValue] = useState(() => {
    const latestNote = sponsorSessionSelfNotes?.slice(-1)[0]
    return latestNote ? latestNote.sponsorSelfNoteText : ""
  })

  // 4b - Current Note Id of the note currently in editor -- 10 Aug 2024 - 14 Jun 2024 (repurposed EtUserTrailSessionImplementation code)
  const [currentSelfNoteId, setCurrentSelfNoteId] = useState(() => {
    const latestNote = sponsorSessionSelfNotes?.slice(-1)[0]
    return latestNote ? latestNote._id : null
  })

  // 4c - Handle Editor Value change
  const handleEditorChange = (value) => {
    setEditorValue(value)
  }

  // 4d - Handle add self note details - 10 Aug 2024
  const handleAddSelfNote = useCallback(() => {
    setEditorValue("")
    setCurrentSelfNoteId(null)
    setIsSelfNoteEditing(true)
    setSelfEditorKey("selfNote-" + Date.now())
  }, [])

  // 4e - Handle edit self note details - 10 Aug 2024
  const handleEditSelfNote = (note) => {
    setEditorValue(note.sponsorSelfNoteText)
    setCurrentSelfNoteId(note._id)
    setIsSelfNoteEditing(true)
  }

  // 4f - Handle edit self note details - 10 Aug 2024
  const handleDeleteSelfNote = async (noteId) => {
    try {
      const updatedNotes = sponsorSessionSelfNotes?.filter(
        (note) => note._id !== noteId
      )

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId,// 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorSessionSelfNotes: updatedNotes,
      }).unwrap()

      setSponsorSelfNotes(updatedNotes) // Update the state with the filtered notes
      alert("Note deleted successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to delete the note:", error)
      // Add error handling logic here if needed
    }
  }

  // 4g - Function to handle saving new Note -- 10 Aug 2024 - 14 Jun 2024 -- (repurposed EtUserTrailSessionImplementation code)
  const handleSaveSelfNote = async () => {
    if (editorValue.trim() === "") {
      alert("The editor is blank. Please enter some text before saving.")
      return
    }

    try {
      let updatedNotes
      if (currentSelfNoteId) {
        // Update existing note
        updatedNotes = sponsorSessionSelfNotes?.map((note) =>
          note._id === currentSelfNoteId
            ? {
                ...note,
                sponsorSelfNoteText: editorValue,
                implementationNoteUpdateDate: new Date(),
              }
            : note
        )
      } else {
        // Add new note
        const newNote = {
          sponsorSelfNoteText: editorValue,
          sponsorSelfNoteDate: new Date(),
          sponsorSelfNoteUpdateDate: new Date(),
        }
        updatedNotes = [...sponsorSessionSelfNotes, newNote]
      }

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId, // 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorSessionSelfNotes: updatedNotes,
      }).unwrap()

      setEditorValue("")
      setIsSelfNoteEditing(false)
      alert("Notes updated successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to update the user session implementation:", error)
      // You can add an error message or any other logic here in case of a failure
    }
  }

  // 4h - Function to handle editing a note -- 14 Jun 2024 --
  const handleCancelEditSelfNote = (note) => {
    setEditorValue("")
    setCurrentSelfNoteId(null)
    setIsSelfNoteEditing(false)
  }

  // 5a - Editor Value setting for All Users Notes based on incoming data
  const [allUsersEditorValue, setAllUsersEditorValue] = useState(() => {
    const latestNote = sponsorAllUsersNotes?.slice(-1)[0]
    return latestNote ? latestNote.sponsorAllUsersNoteText : ""
  })

  // 5b - Current Note Id of the note currently in editor for All Users Notes
  const [currentAllUsersNoteId, setCurrentAllUsersNoteId] = useState(() => {
    const latestNote = sponsorAllUsersNotes?.slice(-1)[0]
    return latestNote ? latestNote._id : null
  })

  // 5c - Handle Editor Value change for All Users Notes
  const handleAllUsersEditorChange = (value) => {
    setAllUsersEditorValue(value)
  }

  // 5d - Handle add All Users note details
  const handleAddAllUsersNote = useCallback(() => {
    setAllUsersEditorValue("")
    setCurrentAllUsersNoteId(null)
    setIsAllUsersNoteEditing(true)
    setAllUsersEditorKey("allUsersNote-" + Date.now())
  }, [])

  // 5e - Handle edit All Users note details
  const handleEditAllUsersNote = (note) => {
    setAllUsersEditorValue(note.sponsorAllUsersNoteText)
    setCurrentAllUsersNoteId(note._id)
    setIsAllUsersNoteEditing(true)
  }

  // 5f - Handle save All Users note details
  const handleDeleteAllUsersNote = async (noteId) => {
    try {
      const updatedNotes = sponsorAllUsersNotes?.filter(
        (note) => note._id !== noteId
      )

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId,// 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorAllUsersNotes: updatedNotes,
      }).unwrap()

      setSponsorAllUsersNotes(updatedNotes) // Update the state with the filtered notes
      alert("Note deleted successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to delete the note:", error)
      // Add error handling logic here if needed
    }
  }

  // 5g - Handle save All Users note details
  const handleSaveAllUsersNote = async () => {
    if (allUsersEditorValue.trim() === "") {
      alert("The editor is blank. Please enter some text before saving.")
      return
    }

    try {
      let updatedNotes
      if (currentAllUsersNoteId) {
        // Update existing note
        updatedNotes = sponsorAllUsersNotes?.map((note) =>
          note._id === currentAllUsersNoteId
            ? {
                ...note,
                sponsorAllUsersNoteText: allUsersEditorValue,
                sponsorAllUsersNoteUpdateDate: new Date(),
              }
            : note
        )
      } else {
        // Add new note
        const newNote = {
          sponsorAllUsersNoteText: allUsersEditorValue,
          sponsorAllUsersNoteDate: new Date(),
          sponsorAllUsersNoteUpdateDate: new Date(),
        }
        updatedNotes = [...sponsorAllUsersNotes, newNote]
      }

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId,// 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorSessionAllUsersNotes: updatedNotes,
      }).unwrap()

      setAllUsersEditorValue("")
      setIsAllUsersNoteEditing(false)
      alert("All Users Notes updated successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to update the All Users notes:", error)
    }
  }

  // 5h - Function to handle editing a All Users note
  const handleCancelEditAllUsersNote = (note) => {
    setAllUsersEditorValue("")
    setCurrentAllUsersNoteId(null)
    setIsAllUsersNoteEditing(false)
  }

  // 6a - Editor Value setting for Self User Notes based on incoming data -- 11 Aug 2024
  const [selfUserEditorValue, setSelfUserEditorValue] = useState(() => {
    const initialEditorValues = {}
    if (sponsorSelfUserNotes) {
      sponsorSelfUserNotes?.forEach((note) => {
        initialEditorValues[note.userId] = note?.sponsorSelfUserNoteText || ""
      })
    }
    return initialEditorValues
  })

  // 6b - Current Note Id of the note currently in editor for Self User Notes -- 11 Aug 2024
  const [currentSelfUserNoteId, setCurrentSelfUserNoteId] = useState({})

  // 6c - Handle Editor Value change for Self User Notes -- 11 Aug 2024
  const handleSelfUserEditorChange = (value, userId) => {
    setSelfUserEditorValue((prev) => ({
      ...prev,
      [userId]: value,
    }))
  }

  // 6d - Handle add Self User note details -- 11 Aug 2024
  const handleAddSelfUserNote = useCallback((userId) => {
    setSelfUserEditorValue((prevState) => ({
      ...prevState,
      [userId]: "", // Clear the editor value for this specific userId
    }))
    setCurrentSelfUserNoteId((prevState) => ({
      ...prevState,
      [userId]: null, // Clear the current note ID for this specific userId
    }))
    setIsSelfUserNoteEditing((prevState) => ({
      ...prevState,
      [userId]: true, // Set editing state to true for this specific userId
    }))
    setSelfUserEditorKey("selfUserNote-" + Date.now())
  }, [])

  // 6e - Handle edit Self User note details -- 11 Aug 2024
  const handleEditSelfUserNote = (userId, note) => {
    setSelfUserEditorValue((prevState) => ({
      ...prevState,
      [userId]: note.sponsorSelfUserNoteText, // Set the editor value to the note's content for this userId
    }))
    setCurrentSelfUserNoteId((prevState) => ({
      ...prevState,
      [userId]: note._id, // Set the current note ID for this specific userId
    }))
    setIsSelfUserNoteEditing((prevState) => ({
      ...prevState,
      [userId]: true, // Set editing state to true for this specific userId
    }))
  }

  // 6f - Handle save Self User note details -- 11 Aug 2024
  const handleDeleteSelfUserNote = async (noteId) => {
    try {
      const updatedNotes = sponsorSelfUserNotes?.filter(
        (note) => note._id !== noteId
      )
      // console.log('updated notes:', updatedNotes)

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId,// 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorSessionSelfUserNotes: updatedNotes,
      }).unwrap()

      setSponsorSelfUserNotes(updatedNotes) // Update the state with the filtered notes
      alert("Note deleted successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to delete the note:", error)
      // Add error handling logic here if needed
    }
  }

  // 6g - Handle save Self User note details -- 11 Aug 2024
  const handleSaveSelfUserNote = async (userId) => {
    // Access the specific note content for the given userId
    const editorContent = selfUserEditorValue[userId]?.trim()
    if (!editorContent || editorContent === "") {
      alert("The editor is blank. Please enter some text before saving.")
      return
    }

    try {
      let updatedNotes
      if (currentSelfUserNoteId[userId]) {
        // Update existing note
        updatedNotes = sponsorSelfUserNotes?.map((note) =>
          note._id === currentSelfUserNoteId[userId]
            ? {
                ...note,
                sponsorSelfUserNoteText: editorContent,
                sponsorSelfUserNoteUpdateDate: new Date(),
              }
            : note
        )
      } else {
        // Add new note
        const newNote = {
          userId: userId,
          sponsorSelfUserNoteText: editorContent,
          sponsorSelfUserNoteDate: new Date(),
          sponsorSelfUserNoteUpdateDate: new Date(),
        }
        updatedNotes = [...sponsorSelfUserNotes, newNote]
      }

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId,// 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorSessionSelfUserNotes: updatedNotes,
      }).unwrap()

      // setSelfUserEditorValue('');
      setSelfUserEditorValue((prevState) => ({
        ...prevState,
        [userId]: "", // Clear the editor value for this userId
      }))
      setIsSelfUserNoteEditing((prevState) => ({
        ...prevState,
        [userId]: false, // Set editing state to false for this userId
      }))
      alert("Self User Note updated successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to update the All Users notes:", error)
    }
  }

  // 6h - Function to handle editing a Self User note
  // const handleCancelEditSelfUserNote = (userId) => {
  //     setSelfUserEditorValue('');
  //     setCurrentSelfUserNoteId(null);
  //     // setIsSelfUserNoteEditing(false);
  //     setIsSelfUserNoteEditing((prevState) => ({
  //         ...prevState,
  //         [userId]: false, // Set editing state to false for this userId
  //     }));
  // };
  const handleCancelEditSelfUserNote = (userId) => {
    setSelfUserEditorValue((prevState) => ({
      ...prevState,
      [userId]: "", // Clear the editor value for this userId
    }))
    setCurrentSelfUserNoteId((prevState) => ({
      ...prevState,
      [userId]: null, // Clear the current note ID for this userId
    }))
    setIsSelfUserNoteEditing((prevState) => ({
      ...prevState,
      [userId]: false, // Set editing state to false for this userId
    }))
  }

  // 6i - Handle toggle show user details -- 11 Aug 2024 -- repurposed from EtUserTrailSessionList -- 23 May 2024 --
  const handleToggleUserDetails = (userId) => {
    setShowUserSectionDetails((prev) => ({
      ...prev,
      [userId]: !prev[userId], // Toggle the visibility of the user section
    }))

    setShowSelfUserNotes((prevNotes) => ({
      ...prevNotes,
      [userId]: !prevNotes[userId], // Toggle visibility for the specific user's notes section
    }))
  }

  // 7a - Editor Value setting for Shared User Notes based on incoming data -- 11 Aug 2024
  const [sharedUserEditorValue, setSharedUserEditorValue] = useState(() => {
    const initialEditorValues = {}
    if (sponsorSharedUserNotes) {
      sponsorSharedUserNotes?.forEach((note) => {
        initialEditorValues[note.userId] = note?.sponsorSharedUserNoteText || ""
      })
    }
    return initialEditorValues
  })

  // 7b - Current Note Id of the note currently in editor for Shared User Notes -- 11 Aug 2024
  const [currentSharedUserNoteId, setCurrentSharedUserNoteId] = useState({})

  // 7c - Handle Editor Value change for Shared User Notes -- 11 Aug 2024
  const handleSharedUserEditorChange = (value, userId) => {
    setSharedUserEditorValue((prev) => ({
      ...prev,
      [userId]: value,
    }))
  }

  // 7d - Handle add Shared User note details -- 11 Aug 2024
  const handleAddSharedUserNote = useCallback((userId) => {
    setSharedUserEditorValue((prevState) => ({
      ...prevState,
      [userId]: "", // Clear the editor value for this specific userId
    }))
    setCurrentSharedUserNoteId((prevState) => ({
      ...prevState,
      [userId]: null, // Clear the current note ID for this specific userId
    }))
    setIsSharedUserNoteEditing((prevState) => ({
      ...prevState,
      [userId]: true, // Set editing state to true for this specific userId
    }))
    setSharedUserEditorKey("sharedUserNote-" + Date.now())
  }, [])

  // 7e - Handle edit Shared User note details -- 11 Aug 2024
  const handleEditSharedUserNote = (userId, note) => {
    setSharedUserEditorValue((prevState) => ({
      ...prevState,
      [userId]: note.sponsorSharedUserNoteText, // Set the editor value to the note's content for this userId
    }))
    setCurrentSharedUserNoteId((prevState) => ({
      ...prevState,
      [userId]: note._id, // Set the current note ID for this specific userId
    }))
    setIsSharedUserNoteEditing((prevState) => ({
      ...prevState,
      [userId]: true, // Set editing state to true for this specific userId
    }))
  }

  // 7f - Handle save Shared User note details -- 11 Aug 2024
  const handleDeleteSharedUserNote = async (noteId) => {
    try {
      const updatedNotes = sponsorSharedUserNotes?.filter(
        (note) => note._id !== noteId
      )
      // console.log('updated notes:', updatedNotes)

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId,// 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorSessionSharedUserNotes: updatedNotes,
      }).unwrap()

      setSponsorSharedUserNotes(updatedNotes) // Update the state with the filtered notes
      alert("Note deleted successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to delete the note:", error)
      // Add error handling logic here if needed
    }
  }

  // 7g - Handle save Shared User note details -- 11 Aug 2024
  const handleSaveSharedUserNote = async (userId) => {
    // Access the specific note content for the given userId
    const editorContent = sharedUserEditorValue[userId]?.trim()
    if (!editorContent || editorContent === "") {
      alert("The editor is blank. Please enter some text before saving.")
      return
    }

    try {
      let updatedNotes
      if (currentSharedUserNoteId[userId]) {
        // Update existing note
        updatedNotes = sponsorSharedUserNotes?.map((note) =>
          note._id === currentSharedUserNoteId[userId]
            ? {
                ...note,
                sponsorSharedUserNoteText: editorContent,
                sponsorSharedUserNoteUpdateDate: new Date(),
              }
            : note
        )
      } else {
        // Add new note
        const newNote = {
          userId: userId,
          sponsorSharedUserNoteText: editorContent,
          sponsorSharedUserNoteDate: new Date(),
          sponsorSharedUserNoteUpdateDate: new Date(),
        }
        updatedNotes = [...sponsorSharedUserNotes, newNote]
      }

      await updateSponsorOrgTrailSessionPad({
        sponsorId,
        sponsorOrgId, // 12 Sep 2024 - changed from orgId
        trailId,
        sessionId,
        sponsorSessionSharedUserNotes: updatedNotes,
      }).unwrap()

      setSharedUserEditorValue((prevState) => ({
        ...prevState,
        [userId]: "", // Clear the editor value for this userId
      }))
      setIsSharedUserNoteEditing((prevState) => ({
        ...prevState,
        [userId]: false, // Set editing state to false for this userId
      }))
      alert("Shared User Note updated successfully")
      window.location.reload()
    } catch (error) {
      console.error("Failed to update the All Users notes:", error)
    }
  }

  // 7h - Function to handle editing a Shared User note
  const handleCancelEditSharedUserNote = (userId) => {
    setSharedUserEditorValue((prevState) => ({
      ...prevState,
      [userId]: "", // Clear the editor value for this userId
    }))
    setCurrentSharedUserNoteId((prevState) => ({
      ...prevState,
      [userId]: null, // Clear the current note ID for this userId
    }))
    setIsSharedUserNoteEditing((prevState) => ({
      ...prevState,
      [userId]: false, // Set editing state to false for this userId
    }))
  }

  // 7i - Handle toggle show user details -- 11 Aug 2024 -- repurposed from EtUserTrailSessionList -- 23 May 2024 --
  const handleToggleSharedUserDetails = (userId) => {
    setShowSharedUserSectionDetails((prev) => ({
      ...prev,
      [userId]: !prev[userId], // Toggle the visibility of the user section
    }))

    setShowSharedUserNotes((prevNotes) => ({
      ...prevNotes,
      [userId]: !prevNotes[userId], // Toggle visibility for the specific user's notes section
    }))
  }

  // 8a - Toggle function to show/hide challenge details - 26 Aug 2024
  const handleToggleChallengeSections = (challengeId) => {
    setShowChallengeSections((prevState) => ({
      ...prevState,
      [challengeId]: !prevState[challengeId],
    }))
  }

  // 8a - Toggle function to show/hide challenge details - 27 Aug 2024
  const handleToggleChallengeHeadSectionDetails = (challengeId) => {
    setShowChallengeHeadSectionDetails((prevState) => ({
      ...prevState,
      [challengeId]: !prevState[challengeId],
    }))
  }

  // 8b - Toggle function to show/hide challenge head details - 26 Aug 2024
  const handleToggleChallengeHeadDetails = (challengeId, headName) => {
    setShowChallengeHeadDetails((prevState) => ({
      ...prevState,
      [`${challengeId}-${headName}`]: !prevState[`${challengeId}-${headName}`],
    }))
  }

  // 8c - Toggle function to show/hide challenge dimension details - 26 Aug 2024
  const handleToggleChallengeDimDetails = (
    challengeId,
    headName,
    dimensionId
  ) => {
    setShowChallengeDimDetails((prevState) => ({
      ...prevState,
      [`${challengeId}-${headName}-${dimensionId}`]:
        !prevState[`${challengeId}-${headName}-${dimensionId}`],
    }))
  }

  // 8d - Toggle Show function for challenge instruction details - 27 Aug 2024
  const handleToggleInstructionDetails = (challengeId) => {
    setShowInstructionDetails((prev) => ({
      ...prev,
      [challengeId]: !prev[challengeId],
    }))
  }

  // 8e - Toggle Show function for challenge instruction details - 03 Sep 2024
  const handleToggleSponsorUserActionUpdate = (actionId) => {
    setShowSponsorUserActionUpdate((prev) => ({
      ...prev,
      [actionId]: !prev[actionId],
    }))
  }

  // 8f - Toggle Show function for sponsor's self note details for all users - 13 Sep 2024
  const handleToggleSponsorSelfAllUsersNoteDetail = (noteId) => {
    setShowSponsorSelfAllUsersNoteDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }

  // 8g - Toggle Show function sponsor's shared note details for all users - 13 Sep 2024
  const handleToggleSponsorAllUsersNoteDetail = (noteId) => {
    setShowSponsorAllUsersNoteDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }
  
  // 8h - Toggle Show function for sponsor's self note details for a specific user - 13 Sep 2024
  const handleToggleSponsorSelfUserNoteDetail = (noteId) => {
    setShowSponsorSelfUserNoteDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }

  // 8i - Toggle Show function for sponsor's shared note details for a specific user - 13 Sep 2024
  const handleToggleSponsorSharedUserNoteDetail = (noteId) => {
    setShowSponsorSharedUserNoteDetail((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }))
  }

  // 9a - Handle Evaluate Action to activate modal - 26 Aug 2024 -- updated 27 Aug 2024 - with setMode and setExistingEvalData

  // 31 Jul 2026
  const handleEvaluate = (challengeId, userId, userOrgId) => {
    const currentUrl = window.location.pathname
    setTriggeringUrl(currentUrl)

    const userCohortId = userCohortIdByUserId[userId]
    // console.log('userCohortId in handleEvaluate', userCohortId)

    if (!userCohortId) {
      alert("This user does not have an active cohort plan for this session.")
      return
    }

    const existingEval = sponsorSessionEvals?.find((evalItem) => {
      const evalData = evalItem?.sessionChallengeSponsorEvalId
      const evalCohortId = evalData?.cohortId?._id || evalData?.cohortId

      return (
        evalData?.challengeId === challengeId &&
        evalData?.userId === userId &&
        evalCohortId === userCohortId
      )
    })

    setIsModalOpen(true)
    setSelectedChallengeId(challengeId)
    setSelectedUserId(userId)
    setSelectedUserOrgId(userOrgId)
    setSelectedCohortId(userCohortId)

    const mode = existingEval ? "update" : "create"
    setMode(mode)
    setExistingEvalData(existingEval ? existingEval.sessionChallengeSponsorEvalId : null)
  }


  // const handleEvaluate = (challengeId, userId, userOrgId) => {
  //   const currentUrl = window.location.pathname // 26 Aug 2024 - Capture the current URL
  //   setTriggeringUrl(currentUrl) // 26 Aug 2024 - Set the triggering URL

  //   // 27 Aug 2024 - Check if there's an existing evaluation for the selected challenge and user
  //   const existingEval = sponsorSessionEvals?.find(
  //     (evalItem) =>
  //       evalItem.sessionChallengeSponsorEvalId.challengeId === challengeId &&
  //       evalItem.sessionChallengeSponsorEvalId.userId === userId
  //   )

  //   setIsModalOpen(true)
  //   // Pass the userId and userOrgId to the modal component
  //   setSelectedChallengeId(challengeId)
  //   setSelectedUserId(userId)
  //   setSelectedUserOrgId(userOrgId)

  //   // Determine the mode and set existing evaluation data if it exists
  //   const mode = existingEval ? "update" : "create"
  //   setMode(mode)
  //   setExistingEvalData(
  //     existingEval ? existingEval.sessionChallengeSponsorEvalId : null
  //   )
  // }
  // console.log("Existing Eval Data", existingEvalData)

  const handleCloseChallengeEvalModal = () => {
    setIsModalOpen(false)
    setTriggeringUrl(null) // 26 Aug 2024 Reset the triggering URL
    setSelectedChallengeId(null)
    setSelectedUserId(null)
    setSelectedUserOrgId(null)
    setSelectedCohortId(null) // 31 Jul 2026
    // window.location.reload(); // Force the page to reload
    // Refetch the data to get the latest evaluations
    refetch()
  }

  // 9b - Handle Create Sponsor Planned Action modal - 02 Sep 2024
  const handleCreatePlannedAction = () => {
    const currentUrl = window.location.pathname // 02 Sep 2024 - Capture the current URL
    setTriggeringActionUrl(currentUrl) // 02 Sep 2024 - Set the triggering URL
    setIsActionModalOpen(true)
  }

  // 10 Sep 2024 - changed the modal to handle create / update
  const handleAddOrUpdatePlannedAction = (action) => {
    const currentUrl = window.location.pathname // 02 Sep 2024 - Capture the current URL
    setTriggeringActionUrl(currentUrl) // 02 Sep 2024 - Set the triggering URL
    setIsActionModalOpen(true)
    
    // Determine the mode based on existing coach planned actions data
    const mode = filteredSponsorUserPlannedActions?.length>0 ? "update" : "create"
    setActionMode(mode)
    setExistingActionData(action)
  }

  const handleCloseCreateActionModal = () => {
    setIsActionModalOpen(false)
    setTriggeringActionUrl(null) // 26 Aug 2024 Reset the triggering URL
    setExistingActionData(null) // 11 Sep 2024
    refetchActions()
  }

  // 10 - Create an object for setting inner HTML
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  // 11 - Function to get the appropriate color class based on the eval score - 26 Aug 2024
  const getColorForScore = (score) => {
    if (score >= 0 && score < 0.9) return "bg-etpx-0"
    if (score >= 0.9 && score < 1.9) return "bg-etpx-1"
    if (score >= 1.9 && score < 2.9) return "bg-etpx-2"
    if (score >= 2.9 && score < 3.9) return "bg-etpx-3"
    if (score >= 3.9 && score < 4.9) return "bg-etpx-4"
    if (score >= 4.9) return "bg-etpx-5" // For score >= 4.9
    return "bg-black" // default
  }

  // 12a - 02 Aug 2026 - Get Selected Evaluation Number
  const getSelectedEvaluationNumber = (
    challengeId,
    evaluationNumbers = []
  ) => {
    return (
      selectedEvaluationNumberByChallenge[
        challengeId
      ] ||
      evaluationNumbers[0] ||
      1
    )
  }

  // 12b - 02 Aug 2026 - Get Challenge Evaluation Numbers
  const getChallengeEvaluationNumbers = (
    challengeId
  ) => {
    return [
      ...new Set(
        sponsorSessionEvals
          ?.map(
            (evalItem) =>
              evalItem
                ?.sessionChallengeSponsorEvalId
          )
          ?.filter(
            (evaluation) =>
              evaluation?.challengeId ===
              challengeId
          )
          ?.map(
            (evaluation) =>
              Number(
                evaluation
                  ?.evaluationNumber || 1
              )
          ) || []
      ),
    ].sort((a, b) => b - a)
  }

  return (
    <div className="m-1 p-1 bg-white rounded font-poppins">
      <div className="m-1 p-1 bg-gray-300 rounded">
        {/* <div className="m-1 p-1" onClick={() => navigate("/userMagicHome")}> */}
         <div className="m-1 p-1" onClick={() => navigate("/etapp")}> {/* 23 Feb 2025*/}
          <ArrowLeftCircleIcon className="h-5 w-5 text-orange-600 cursor-pointer" />
        </div>
        {/* Sponsor Header */}
        <div className="grid grid-cols-12 justify-between items-center m-1 p-1">
          <div className="col-span-3 flex justify-start items-center m-1 p-1">
            <div
              onClick={() => setShowDetails((prev) => !prev)}
              className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
            >
              {showDetails ? (
                <MinusIcon className="h-5 w-5 text-orange-600" />
              ) : (
                <PlusIcon className="h-5 w-5 text-orange-600" />
              )}
            </div>
            <div>
              <User username={username} defaultUserPic={userProfileIcon2} />
            </div>
            <div>
              <p className="px-2 mx-2 text-gray-600 font-bold">{name}</p>
              <p className="mx-2 text-[10px] text-gray-400 rounded-full inline-block px-2 border bg-white shadow shadow-gray-400">
                {sponsorOrgRole} & {sponsorOrgTitle},{" "}
                <span className="text-orange-600">{sponsorOrgName}</span>
              </p>
            </div>
          </div>
          <div className="col-span-2 flex justify-start items-center">
            <div>
              <p className="text-[10px] text-gray-500 mx-1 px-1">Org:</p>
            </div>
            <div>
              <BuildingOffice2Icon className="h-5 w-5 text-orange-600 m-1" />
            </div>
            <div>
              {/* <p className=" text-gray-600 font-bold">{orgName}</p> */}
              {/* 18 May 2025 - changed to sponsorOrgName*/}
              <p className=" text-gray-600 font-bold">{sponsorOrgName}</p>
            </div>
          </div>
          <div className="col-span-2 flex justify-start items-center">
            <div>
              <p className="text-[10px] text-gray-500 mx-1 px-1">Trail:</p>
            </div>
            <div>
              <ArrowTrendingUpIcon className="h-5 w-5 text-orange-600 m-1" />
            </div>
            <div className="flex justify-start items-center">
              <div className="flex justify-center item-center rounded-full h-6 w-6 bg-gray-200 hover:bg-gray-50 mx-1">
                <img src={trailPic} alt="" className="h-5 w-5 " />
              </div>
              <div>
                <p className="text-gray-600 font-bold">{trailName}</p>
              </div>
            </div>
          </div>
          <div className="col-span-4 flex justify-start items-center">
            <div>
              <p className="text-[10px] text-gray-500 mx-1 px-1">Session:</p>
            </div>
            <div>
              <CalendarDaysIcon className="h-5 w-5 text-orange-600 m-1" />
            </div>
            <div className="flex justify-start items-center">
              <div className="flex justify-center item-center rounded-full h-6 w-6 bg-gray-200 hover:bg-gray-50 mx-1">
                <img src={sessionPic} alt="" className="h-5 w-5 " />
              </div>
              <div>
                <p className="px-2 mx-2 text-gray-600 font-bold">
                  {sessionName}
                </p>
              </div>
            </div>
          </div>
          <div className="col-span-1 flex justify-start items-center">
            <div>
              <p className="text-[10px] text-gray-500 mx-1 px-1">Users :</p>
            </div>
            <div>
              <UserGroupIcon className="h-5 w-5 text-orange-600 m-1" />
            </div>
            <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50">
              <p className="text-orange-600 text-[10px]">{userCount}</p>
            </div>
          </div>
        </div>
        {showDetails && (
          <div className="m-1 p-1 bg-white rounded">
            {/* Section 1 - Session Info */}
            <div className="m-1 p-1 bg-gray-100 rounded">
              <div className="flex justify-between items-center m-1 p-1">
                <div className="flex justify-start items-center m-1 p-1">
                  <div
                    onClick={() => setShowSessionInfo((prev) => !prev)}
                    className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                  >
                    {showSessionInfo ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div className="mx-1 px-1">
                    <InformationCircleIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-md font-bold text-gray-600 mx-2 px-2">
                      Session Info
                    </p>
                  </div>
                </div>
              </div>
              {showSessionInfo && sessionDetails?.sessionIntro && (
                <div className="bg-white m-1 p-1 rounded">
                  <div className="p-2 grid grid-cols-3 gap-2 justify-center">
                    <div className="mx-2 border-r  border-orange-200 col-span-1 h-full">
                      <div className="flex flex-col justify-start">
                        <div className="flex justify-start items-center  m-1 p-1">
                          <div className="">
                            <Square3Stack3DIcon className="h-5 w-5 text-orange-600" />
                          </div>
                          <p className="m-1 p-1 text-xs text-gray-400 font-bold border-b-2 border-orange-600">
                            Session{" "}
                            <span className="text-orange-600">Detail</span>
                          </p>
                        </div>
                        <p
                          dangerouslySetInnerHTML={createMarkup(
                            sessionDetails?.sessionIntro
                          )}
                          className="fix-nested-lists text-[10px] text-gray-600"
                        ></p>
                      </div>
                    </div>

                    <div className="mx-2 col-span-1 border-r border-orange-200 h-full">
                      <div className="flex flex-col justify-start">
                        <div className="flex justify-start items-center  m-1 p-1">
                          <div className="">
                            <ListBulletIcon className="h-5 w-5 text-orange-600" />
                          </div>
                          <p className="m-1 p-1 text-xs text-gray-400 font-bold border-b-2 border-orange-600">
                            Session{" "}
                            <span className="text-orange-600">Prework</span>
                          </p>
                        </div>

                        {sessionDetails?.sessionPreWork &&
                          sessionDetails?.sessionPreWork?.map(
                            (item, preWorkIndex) => (
                              <div
                                key={preWorkIndex}
                                className="rounded m-1 p-1 bg-gray-100"
                              >
                                <p className="text-[10px] mx-1">
                                  <span className="font-bold"></span>
                                  {item.prework}
                                </p>
                              </div>
                            )
                          )}
                      </div>
                    </div>

                    <div className="mx-2 col-span-1">
                      <div className="flex flex-col justify-start">
                        <div className="flex justify-start items-center m-1 p-1">
                          <div className="">
                            <SwatchIcon className="h-5 w-5 text-orange-600" />
                          </div>
                          <p className="m-1 p-1 text-xs text-gray-400 font-bold border-b-2 border-orange-600">
                            Session{" "}
                            <span className="text-orange-600">Outcomes</span>
                          </p>
                        </div>

                        {sessionDetails?.sessionOutcomes &&
                          sessionDetails?.sessionOutcomes?.map(
                            (item, outIndex) => (
                              <div
                                key={outIndex}
                                className="rounded m-1 p-1 bg-gray-100"
                              >
                                <p className="text-[10px] mx-1">
                                  {item?.outcome}
                                </p>
                              </div>
                            )
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Section 2 - Session Users */}
            <div className="m-1 p-1 bg-gray-100 rounded">
              <div className="flex justify-between items-center m-1 p-1">
                <div className="flex justify-start items-center m-1 p-1">
                  <div
                    onClick={() => setShowSessionUsers((prev) => !prev)}
                    className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                  >
                    {showSessionUsers ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div className="mx-1 px-1">
                    <UserGroupIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-md font-bold text-gray-600 mx-2 px-2">
                      Session Users
                    </p>
                  </div>
                  <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50">
                    <p className="text-orange-600 text-[10px]">{userCount}</p>
                  </div>
                </div>
              </div>

              {showSessionUsers &&
                filteredSessionUsers?.map((user, userIndex) => {              // 10 Sep 2024 - changed from sessionUsers to filteredSessionUsers
                  return (
                    <div key={userIndex} className="m-1 p-1 bg-gray-50 rounded">
                      <div className="flex justify-between items-center m-1 p-1">
                        <div className=" flex justify-start items-center m-1 p-1">
                          <div className="mx-1 px-1">
                            <User
                              username={user.userId?.username}
                              defaultUserPic={userProfileIcon2}
                            />
                          </div>
                          <div className="mx-1 px-1">
                            <p className="text-xs text-gray-600 font-bold">
                              {user.userId?.name}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {user.userId?.email}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>

            {/* Section 3 - Sponsor's Session Self Notes */}
            <div className="m-1 p-1 bg-gray-100 rounded">
              <div className="flex justify-between items-center m-1 p-1">
                <div className="flex justify-start items-center m-1 p-1">
                  <div
                    onClick={() => setShowSelfNotes((prev) => !prev)}
                    className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                  >
                    {showSelfNotes ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div>
                    <EtDocSelfNoteAllUsersIconSVGComp className="h-7 w-7 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-md font-bold text-gray-600 mx-2 px-2">
                      Sponsor's Session Notes - Self
                    </p>
                    <p className="text-[10px] italic text-gray-600 mx-2 px-2">
                      Capture your session experiences, thoughts and
                      observations about the participants.{" "}
                    </p>
                  </div>
                </div>
              </div>

              {showSelfNotes && (
                <div className="m-2 p-2 bg-gray-300 rounded">
                  <div className="m-1 p-1 border border-white rounded">
                    {sponsorSelfNotes?.length === 0 && (
                      <div className=" ">
                        <p className="text-[11px] text-gray-500 italic mx-2 px21">
                          No self notes available. Add your first note below.
                        </p>
                      </div>
                    )}

                    {/* Add Note Form */}
                    {isSelfNoteEditing ? (
                      <div className="bg-gray-50 p-1 m-1 rounded">
                        <EtContentEditorLight2
                          value={editorValue}
                          handleChange={handleEditorChange}
                          placeholderText="Write your self note here..."
                          editorKey={selfEditorKey}
                        />
                        <div className="flex justify-start items-center m-1 p-1">
                          <button
                            type="button"
                            onClick={handleSaveSelfNote}
                            className="w-16 text-white rounded-full bg-lime-600 hover:bg-lime-700"
                          >
                            <p className="text-[10px] px-2 py-1">Save</p>
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEditSelfNote}
                            className="w-16 text-white rounded-full bg-orange-600 hover:bg-orange-700 mx-1"
                          >
                            <p className="text-[10px] px-2 py-1">Cancel</p>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-start items-center m-1 p-1">
                        <p className="font-bold text-xs m-1 p-1">Add</p>
                        <button type="button" onClick={handleAddSelfNote}>
                          <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
                        </button>
                      </div>
                    )}

                    {/* Session Self Note Trail */}

                    <div className="m-1 p-1">
                      <div>
                        <p className="m-1 p-1 text-xs font-bold">
                          Session Notes Trail:
                        </p>
                      </div>
                      <div>
                        {sponsorSelfNotes
                          ?.slice()
                          .sort(
                            (a, b) =>
                              new Date(b.sponsorSelfNoteDate) -
                              new Date(a.sponsorSelfNoteDate)
                          ) // Sort in descending order
                          .map((note, noteIndex, arr) => (
                            <div
                              key={note._id}
                              className="bg-white p-1 m-1 rounded"
                            >
                              {/* 13 Sep 2024*/}
                              <RenderNotes 
                                  note={note}
                                  noteTextKey="sponsorSelfNoteText"
                                  updateDateKey="sponsorSelfNoteUpdateDate"
                                  createdDateKey="sponsorSelfNoteDate"
                                  showDetail={showSponsorSelfAllUsersNoteDetail}
                                  toggleShowDetail={handleToggleSponsorSelfAllUsersNoteDetail}
                                  handleEditNote={handleEditSelfNote}
                                  handleDeleteNote={handleDeleteSelfNote}
                                  noteIndex={noteIndex}
                                  arr={arr}
                                  createMarkup={createMarkup}
                                />
                              {/* <div className="bg-gray-100 m-1 p-1 rounded ">
                                <div className="grid grid-cols-12 justify-between items-center m-1 p-1">
                                  <div className="col-span-1 flex justify-start items-center">
                                    <div>
                                      <p className="text-[11px] font-bold">
                                        {arr.length - noteIndex}.
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18">
                                        {getTimeSince(
                                          note?.sponsorSelfNoteUpdateDate
                                        )}
                                      </p>
                                      <p className="text-[7px] mx-1 italic px-2 py-1 text-gray-400">
                                        last updated
                                      </p>
                                    </div>
                                  </div>

                                  <div className="col-span-7 flex justify-start items-center">
                                    <div
                                      dangerouslySetInnerHTML={createMarkup(
                                        note.sponsorSelfNoteText
                                      )}
                                      className="text-[11px] text-gray-700 m-1 p-1"
                                    ></div>
                                  </div>

                                  <div className="col-span-3 flex justify-start items-center mx-2">
                                    <ClockIcon className="h-5 w-5 text-lime-600" />
                                    <p className="text-[9px] mx-1">
                                      First Created:
                                    </p>
                                    <p className="text-gray-400 text-[9px] mx-2">
                                      {formatDateTime(note.sponsorSelfNoteDate)}
                                    </p>
                                  </div>

                                  <div className="col-span-1 flex justify-start items-center mx-2">
                                    <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                      <PencilSquareIcon
                                        className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                        onClick={() => handleEditSelfNote(note)}
                                      />
                                    </div>
                                    <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                      <TrashIcon
                                        className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                        onClick={() =>
                                          handleDeleteSelfNote(note._id)
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div> */}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Section 4 - Sponsor's Session All Users Notes */}
            <div className="m-1 p-1 bg-gray-100 rounded">
              <div className="flex justify-between items-center m-1 p-1">
                <div className="flex justify-start items-center m-1 p-1">
                  <div
                    onClick={() => setShowAllUsersNotes((prev) => !prev)}
                    className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                  >
                    {showAllUsersNotes ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div>
                    <EtDocSharedNoteAllUsersIconSVGComp className="h-7 w-7 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-md font-bold text-gray-600 mx-2 px-2">
                      Sponsor's Session Notes - All Users
                    </p>
                    <p className="text-[10px] italic text-gray-600 mx-2 px-2">
                      Capture your session experiences, thoughts and
                      observations about the participants which you can share
                      with them.{" "}
                    </p>
                  </div>
                </div>
              </div>

              {showAllUsersNotes && (
                <div className="m-2 p-2 bg-gray-300 rounded">
                  <div className="m-1 p-1 border border-white rounded">
                    {sponsorAllUsersNotes?.length === 0 && (
                      <div className=" ">
                        <p className="text-[11px] text-gray-500 italic mx-2 px21">
                          No all users notes available. Add your first note
                          below.
                        </p>
                      </div>
                    )}

                    {/* Add Note Form */}
                    {isAllUsersNoteEditing ? (
                      <div className="bg-gray-50 p-1 m-1 rounded">
                        <EtContentEditorLight2
                          value={allUsersEditorValue}
                          handleChange={handleAllUsersEditorChange}
                          placeholderText="Write your all users note here..."
                          editorKey={allUsersEditorKey}
                        />
                        <div className="flex justify-start items-center m-1 p-1">
                          <button
                            type="button"
                            onClick={handleSaveAllUsersNote}
                            className="w-16 text-white rounded-full bg-lime-600 hover:bg-lime-700"
                          >
                            <p className="text-[10px] px-2 py-1">Save</p>
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEditAllUsersNote}
                            className="w-16 text-white rounded-full bg-orange-600 hover:bg-orange-700 mx-1"
                          >
                            <p className="text-[10px] px-2 py-1">Cancel</p>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-start items-center m-1 p-1">
                        <p className="font-bold text-xs m-1 p-1">Add</p>
                        <button type="button" onClick={handleAddAllUsersNote}>
                          <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
                        </button>
                      </div>
                    )}

                    {/* Session AllUsers Note Trail */}

                    <div className="m-1 p-1">
                      <div>
                        <p className="m-1 p-1 text-xs font-bold">
                          Session Notes Trail:
                        </p>
                      </div>
                      <div>
                        {sponsorAllUsersNotes
                          ?.slice()
                          .sort(
                            (a, b) =>
                              new Date(b.sponsorAllUsersNoteDate) -
                              new Date(a.sponsorAllUsersNoteDate)
                          ) // Sort in descending order
                          .map((note, noteIndex, arr) => (
                            <div
                              key={note._id}
                              className="bg-white p-1 m-1 rounded"
                            >
                              <RenderNotes 
                                  note={note}
                                  noteTextKey="sponsorAllUsersNoteText"
                                  updateDateKey="sponsorAllUsersNoteUpdateDate"
                                  createdDateKey="sponsorAllUsersNoteDate"
                                  showDetail={showSponsorAllUsersNoteDetail}
                                  toggleShowDetail={handleToggleSponsorAllUsersNoteDetail}
                                  handleEditNote={handleEditAllUsersNote}
                                  handleDeleteNote={handleDeleteAllUsersNote}
                                  noteIndex={noteIndex}
                                  arr={arr}
                                  createMarkup={createMarkup}
                                /> {/* 13 Sep 2024*/}
                              {/* <div className="bg-gray-100 m-1 p-1 rounded ">
                                <div className="grid grid-cols-12 justify-between items-center m-1 p-1">
                                  <div className="col-span-1 flex justify-start items-center">
                                    <div>
                                      <p className="text-[11px] font-bold">
                                        {arr.length - noteIndex}.
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18">
                                        {getTimeSince(
                                          note?.sponsorAllUsersNoteUpdateDate
                                        )}
                                      </p>
                                      <p className="text-[7px] mx-1 italic px-2 py-1 text-gray-400">
                                        last updated
                                      </p>
                                    </div>
                                  </div>

                                  <div className="col-span-7 flex justify-start items-center">
                                    <div
                                      dangerouslySetInnerHTML={createMarkup(
                                        note.sponsorAllUsersNoteText
                                      )}
                                      className="text-[11px] text-gray-700 m-1 p-1"
                                    ></div>
                                  </div>

                                  <div className="col-span-3 flex justify-start items-center mx-2">
                                    <ClockIcon className="h-5 w-5 text-lime-600" />
                                    <p className="text-[9px] mx-1">
                                      First Created:
                                    </p>
                                    <p className="text-gray-400 text-[9px] mx-2">
                                      {formatDateTime(
                                        note.sponsorAllUsersNoteDate
                                      )}
                                    </p>
                                  </div>

                                  <div className="col-span-1 flex justify-start items-center mx-2">
                                    <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                      <PencilSquareIcon
                                        className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                        onClick={() =>
                                          handleEditAllUsersNote(note)
                                        }
                                      />
                                    </div>
                                    <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                      <TrashIcon
                                        className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                        onClick={() =>
                                          handleDeleteAllUsersNote(note._id)
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div> */}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Section 5 - Sponsor's Session Self User Specific Notes */}
            <div className="m-1 p-1 bg-gray-100 rounded">
              <div className="flex justify-between items-center m-1 p-1">
                <div className="flex justify-start items-center m-1 p-1">
                  <div
                    onClick={() => setShowUserSections((prev) => !prev)}
                    className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                  >
                    {showUserSections ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div className="">
                    <EtDocSelfUserNoteIconSVGComp className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-md font-bold text-gray-600 mx-2 px-2">
                      Sponsor's Session Notes - Self - Specific User
                    </p>
                    <p className="text-[10px] italic text-gray-600 mx-2 px-2">
                      Capture your session experiences, thoughts and
                      observations about specific participants.{" "}
                    </p>
                  </div>
                </div>
              </div>
              {showUserSections &&
                filteredSessionUsers &&                    // 10 Sep 2024 - changed from sessionUsers to filteredSessionUsers
                filteredSessionUsers?.map((user, userIndex) => {
                  const userId = user.userId?._id
                  // console.log('user id:', userId)

                  return (
                    <div key={userIndex} className="m-1 p-1 bg-gray-50 rounded">
                      <div className="flex justify-between items-center m-1 p-1">
                        <div className=" flex justify-start items-center m-1 p-1">
                          <div
                            onClick={() => handleToggleUserDetails(userId)}
                            className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                          >
                            {showUserSectionDetails[userId] ? (
                              <MinusIcon className="h-5 w-5 text-orange-600" />
                            ) : (
                              <PlusIcon className="h-5 w-5 text-orange-600" />
                            )}
                          </div>

                          <div className="mx-1 px-1">
                            <User
                              username={user.userId?.username}
                              defaultUserPic={userProfileIcon2}
                            />
                          </div>
                          <div className="mx-1 px-1">
                            <p className="text-xs text-gray-600 font-bold">
                              {user.userId?.name}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {user.userId?.email}
                            </p>
                          </div>
                        </div>
                      </div>

                      {showUserSectionDetails[userId] &&
                        showSelfUserNotes[userId] && (
                          <div className="m-2 p-2 bg-gray-300 rounded">
                            <div className="m-1 p-1 border border-white rounded">
                              {sponsorSelfUserNotes?.filter(
                                (note) => note.userId === userId
                              )?.length === 0 && (
                                <div className=" ">
                                  <p className="text-[11px] text-gray-500 italic mx-2 px21">
                                    No users notes available. Add your first
                                    note below by selecting a user from the list
                                    below.
                                  </p>
                                </div>
                              )}

                              {/* Add Note Form */}
                              {isSelfUserNoteEditing[userId] ? (
                                <div className="bg-gray-50 p-1 m-1 rounded">
                                  <EtContentEditorLight2
                                    value={selfUserEditorValue[userId]}
                                    handleChange={(value) =>
                                      handleSelfUserEditorChange(value, userId)
                                    }
                                    placeholderText="Write your all users note here..."
                                    editorKey={selfUserEditorKey + userId}
                                  />
                                  <div className="flex justify-start items-center m-1 p-1">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleSaveSelfUserNote(userId)
                                      }
                                      className="w-16 text-white rounded-full bg-lime-600 hover:bg-lime-700"
                                    >
                                      <p className="text-[10px] px-2 py-1">
                                        Save
                                      </p>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleCancelEditSelfUserNote(userId)
                                      }
                                      className="w-16 text-white rounded-full bg-orange-600 hover:bg-orange-700 mx-1"
                                    >
                                      <p className="text-[10px] px-2 py-1">
                                        Cancel
                                      </p>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex justify-start items-center m-1 p-1">
                                  <p className="font-bold text-xs m-1 p-1">
                                    Add
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddSelfUserNote(userId)
                                    }
                                  >
                                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
                                  </button>
                                </div>
                              )}

                              {/* Session SelfUser Note Trail */}

                              <div className="m-1 p-1">
                                <div>
                                  <p className="m-1 p-1 text-xs font-bold">
                                    Session Notes Trail:
                                  </p>
                                </div>
                                <div>
                                  {sponsorSelfUserNotes
                                    ?.filter((note) => note.userId === userId)
                                    ?.slice()
                                    .sort(
                                      (a, b) =>
                                        new Date(b.sponsorSelfUserNoteDate) -
                                        new Date(a.sponsorSelfUserNoteDate)
                                    ) // Sort in descending order
                                    .map((note, noteIndex, arr) => (
                                      <div
                                        key={note._id}
                                        className="bg-white p-1 m-1 rounded"
                                      >
                                        <RenderNotes 
                                          note={note}
                                          noteTextKey="sponsorSelfUserNoteText"
                                          updateDateKey="sponsorSelfUserNoteUpdateDate"
                                          createdDateKey="sponsorSelfUserNoteDate"
                                          showDetail={showSponsorSelfUserNoteDetail}
                                          toggleShowDetail={handleToggleSponsorSelfUserNoteDetail}
                                          handleEditNote={handleEditSelfUserNote}
                                          handleDeleteNote={handleDeleteSelfUserNote}
                                          noteIndex={noteIndex}
                                          userId={userId}
                                          arr={arr}
                                          createMarkup={createMarkup}
                                        />  {/* 13 Sep 2024*/}
                                        {/* <div className="bg-gray-100 m-1 p-1 rounded ">
                                          <div className="grid grid-cols-12 justify-between items-center m-1 p-1">
                                            <div className="col-span-1 flex justify-start items-center">
                                              <div>
                                                <p className="text-[11px] font-bold">
                                                  {arr.length - noteIndex}.
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18">
                                                  {getTimeSince(
                                                    note?.sponsorSelfUserNoteUpdateDate
                                                  )}
                                                </p>
                                                <p className="text-[7px] mx-1 italic px-2 py-1 text-gray-400">
                                                  last updated
                                                </p>
                                              </div>
                                            </div>

                                            <div className="col-span-7 flex justify-start items-center">
                                              <div
                                                dangerouslySetInnerHTML={createMarkup(
                                                  note.sponsorSelfUserNoteText
                                                )}
                                                className="text-[11px] text-gray-700 m-1 p-1"
                                              ></div>
                                            </div>

                                            <div className="col-span-3 flex justify-start items-center mx-2">
                                              <ClockIcon className="h-5 w-5 text-lime-600" />
                                              <p className="text-[9px] mx-1">
                                                First Created:
                                              </p>
                                              <p className="text-gray-400 text-[9px] mx-2">
                                                {formatDateTime(
                                                  note.sponsorSelfUserNoteDate
                                                )}
                                              </p>
                                            </div>

                                            <div className="col-span-1 flex justify-start items-center mx-2">
                                              <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                                <PencilSquareIcon
                                                  className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                                  onClick={() =>
                                                    handleEditSelfUserNote(
                                                      userId,
                                                      note
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                                <TrashIcon
                                                  className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                                  onClick={() =>
                                                    handleDeleteSelfUserNote(
                                                      note._id
                                                    )
                                                  }
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div> */}
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )
                })}
            </div>

            {/* Section 6 - Sponsor's Session Shared User Specific Notes */}
            <div className="m-1 p-1 bg-gray-100 rounded">
              <div className="flex justify-between items-center m-1 p-1">
                <div className="flex justify-start items-center m-1 p-1">
                  <div
                    onClick={() => setShowSharedUserSections((prev) => !prev)}
                    className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                  >
                    {showSharedUserSections ? (
                      <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                  </div>
                  <div>
                    <EtDocSharedUserNoteIconSVGComp className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-md font-bold text-gray-600 mx-2 px-2">
                      Sponsor's Session Notes - Shared - User Specific
                    </p>
                    <p className="text-[10px] italic text-gray-600 mx-2 px-2">
                      Capture your session experiences, thoughts and
                      observations about specific participants which you would
                      share with them.{" "}
                    </p>
                  </div>
                </div>
              </div>
              {showSharedUserSections &&
                filteredSessionUsers &&               // 10 Sep 2024 - changed from sessionUsers to filteredSessionUsers
                filteredSessionUsers?.map((user, userIndex) => {
                  const userId = user.userId?._id
                  // console.log('user id:', userId)

                  return (
                    <div key={userIndex} className="m-1 p-1 bg-gray-50 rounded">
                      <div className="flex justify-between items-center m-1 p-1">
                        <div className=" flex justify-start items-center m-1 p-1">
                          <div
                            onClick={() =>
                              handleToggleSharedUserDetails(userId)
                            }
                            className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                          >
                            {showSharedUserSectionDetails[userId] ? (
                              <MinusIcon className="h-5 w-5 text-orange-600" />
                            ) : (
                              <PlusIcon className="h-5 w-5 text-orange-600" />
                            )}
                          </div>

                          <div className="mx-1 px-1">
                            <User
                              username={user.userId?.username}
                              defaultUserPic={userProfileIcon2}
                            />
                          </div>
                          <div className="mx-1 px-1">
                            <p className="text-xs text-gray-600 font-bold">
                              {user.userId?.name}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {user.userId?.email}
                            </p>
                          </div>
                        </div>
                      </div>

                      {showSharedUserSectionDetails[userId] &&
                        showSharedUserNotes[userId] && (
                          <div className="m-2 p-2 bg-gray-300 rounded">
                            <div className="m-1 p-1 border border-white rounded">
                              {sponsorSharedUserNotes?.filter(
                                (note) => note.userId === userId
                              )?.length === 0 && (
                                <div className=" ">
                                  <p className="text-[11px] text-gray-500 italic mx-2 px21">
                                    No users notes available. Add your first
                                    note below by selecting a user from the list
                                    below.
                                  </p>
                                </div>
                              )}

                              {/* Add Note Form */}
                              {isSharedUserNoteEditing[userId] ? (
                                <div className="bg-gray-50 p-1 m-1 rounded">
                                  <EtContentEditorLight2
                                    value={sharedUserEditorValue[userId]}
                                    handleChange={(value) =>
                                      handleSharedUserEditorChange(
                                        value,
                                        userId
                                      )
                                    }
                                    placeholderText="Write your all users note here..."
                                    editorKey={sharedUserEditorKey + userId}
                                  />
                                  <div className="flex justify-start items-center m-1 p-1">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleSaveSharedUserNote(userId)
                                      }
                                      className="w-16 text-white rounded-full bg-lime-600 hover:bg-lime-700"
                                    >
                                      <p className="text-[10px] px-2 py-1">
                                        Save
                                      </p>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleCancelEditSharedUserNote(userId)
                                      }
                                      className="w-16 text-white rounded-full bg-orange-600 hover:bg-orange-700 mx-1"
                                    >
                                      <p className="text-[10px] px-2 py-1">
                                        Cancel
                                      </p>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex justify-start items-center m-1 p-1">
                                  <p className="font-bold text-xs m-1 p-1">
                                    Add
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddSharedUserNote(userId)
                                    }
                                  >
                                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
                                  </button>
                                </div>
                              )}

                              {/* Session SharedUser Note Trail */}

                              <div className="m-1 p-1">
                                <div>
                                  <p className="m-1 p-1 text-xs font-bold">
                                    Session Notes Trail:
                                  </p>
                                </div>
                                <div>
                                  {sponsorSharedUserNotes
                                    ?.filter((note) => note.userId === userId)
                                    ?.slice()
                                    .sort(
                                      (a, b) =>
                                        new Date(b.sponsorSharedUserNoteDate) -
                                        new Date(a.sponsorSharedUserNoteDate)
                                    ) // Sort in descending order
                                    .map((note, noteIndex, arr) => (
                                      <div
                                        key={note._id}
                                        className="bg-white p-1 m-1 rounded"
                                      >
                                        <RenderNotes 
                                          note={note}
                                          noteTextKey="sponsorSharedUserNoteText"
                                          updateDateKey="sponsorSharedUserNoteUpdateDate"
                                          createdDateKey="sponsorSharedUserNoteDate"
                                          showDetail={showSponsorSharedUserNoteDetail}
                                          toggleShowDetail={handleToggleSponsorSharedUserNoteDetail}
                                          handleEditNote={handleEditSharedUserNote}
                                          handleDeleteNote={handleDeleteSharedUserNote}
                                          noteIndex={noteIndex}
                                          userId={userId}
                                          arr={arr}
                                          createMarkup={createMarkup}
                                        /> {/* 13 Sep 2024*/}
                                        {/* <div className="bg-gray-100 m-1 p-1 rounded ">
                                          <div className="grid grid-cols-12 justify-between items-center m-1 p-1">
                                            <div className="col-span-1 flex justify-start items-center">
                                              <div>
                                                <p className="text-[11px] font-bold">
                                                  {arr.length - noteIndex}.
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18">
                                                  {getTimeSince(
                                                    note?.sponsorSharedUserNoteUpdateDate
                                                  )}
                                                </p>
                                                <p className="text-[7px] mx-1 italic px-2 py-1 text-gray-400">
                                                  last updated
                                                </p>
                                              </div>
                                            </div>

                                            <div className="col-span-7 flex justify-start items-center">
                                              <div
                                                dangerouslySetInnerHTML={createMarkup(
                                                  note.sponsorSharedUserNoteText
                                                )}
                                                className="text-[11px] text-gray-700 m-1 p-1"
                                              ></div>
                                            </div>

                                            <div className="col-span-3 flex justify-start items-center mx-2">
                                              <ClockIcon className="h-5 w-5 text-lime-600" />
                                              <p className="text-[9px] mx-1">
                                                First Created:
                                              </p>
                                              <p className="text-gray-400 text-[9px] mx-2">
                                                {formatDateTime(
                                                  note.sponsorSharedUserNoteDate
                                                )}
                                              </p>
                                            </div>

                                            <div className="col-span-1 flex justify-start items-center mx-2">
                                              <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                                <PencilSquareIcon
                                                  className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                                  onClick={() =>
                                                    handleEditSharedUserNote(
                                                      userId,
                                                      note
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                                <TrashIcon
                                                  className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                                  onClick={() =>
                                                    handleDeleteSharedUserNote(
                                                      note._id
                                                    )
                                                  }
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div> */}
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )
                })}
            </div>

            {/* Section 7 - Sponsor's Session Challenge Evaluation - 25 Aug 2024*/}
            {/* 03 Aug 2026 */}
            <EtSponsorSessionChallengeEvaluations
              sessionChallenges={sessionChallenges || []}
              sessionUsers={sessionUsers || []}
              sponsorSessionEvals={sponsorSessionEvals || []}
              sponsorId={sponsorId}
              sponsorOrgId={sponsorOrgId}
              trailId={trailId}
              sessionId={sessionId}
              userCohortIdByUserId={userCohortIdByUserId}
              refetch={refetch}
            />
            
             {/* Section 8 - Sponsor's Session Planned User Actions - 01 Sep 2024*/}
             <div className="m-1 p-1 bg-gray-100 rounded">
                <div className="flex justify-between items-center m-1 p-1">
                  <div className="flex justify-start items-center m-1 p-1">
                    <div
                      onClick={() => setShowSponsorAllUsersActionsSection((prev) => !prev)}
                      className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                    >
                      {showSponsorAllUsersActionsSection ? (
                        <MinusIcon className="h-5 w-5 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-5 w-5 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <EtSponsorUserActionIcon1SVGComp className="h-7 w-7 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-md font-bold text-gray-600 mx-2 px-2">
                        Sponsor's Session Planned Actions
                      </p>
                      <p className="text-[10px] italic text-gray-600 mx-2 px-2">
                        Plan and share actions for all session users
                      </p>
                    </div>
                  </div>
                </div>

                {showSponsorAllUsersActionsSection && (
                  <div className="m-1 p-1">
                    {/* Add Planned Actions Button */}

                    <div className="flex justify-start items-center m-1 p-1">
                        <p className="font-bold text-xs m-1 p-1">Add</p>
                        <button type="button" onClick={handleCreatePlannedAction}>
                          <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-lime-600 hover:bg-green-600" />
                        </button>
                      </div>

                      <div className="m-1 p-1 bg-gray-200 rounded">
                          {/* Planned Actions Header */}
                          <div className="flex justify-start items-center m-1 p-1">
                            <div
                              onClick={() => setShowSponsorAllUsersCreatedActions((prev) => !prev)}
                              className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                            >
                              {showSponsorAllUsersCreatedActions ? (
                                <MinusIcon className="h-5 w-5 text-orange-600" />
                              ) : (
                                <PlusIcon className="h-5 w-5 text-orange-600" />
                              )}
                            </div>
                            <div>
                                <p className="text-gray-600 font-bold mx-1 px-1">Sponsor's Planned Actions</p>
                            </div>
                          </div>

                          {showSponsorAllUsersCreatedActions && filteredSponsorUserPlannedActions && filteredSponsorUserPlannedActions?.length>0 &&  filteredSponsorUserPlannedActions?.map((action, actionIndex) => {
                            // console.log('planned action:', action)
                            return(
                              <div key={action._id+actionIndex} className="bg-white rounded m-1 p-1">
                                <div className="">  {/* original - bg-gray-50 rounded */}
                                  <div className="grid grid-cols-8 justify-between items-center">
                                    <div className="col-span-5 flex justify-start items-center">

                                          <div
                                            onClick={() => handleToggleSponsorUserActionUpdate(action._id)}
                                            className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer"
                                          >
                                            {showSponsorUserActionUpdate[action._id] ? (
                                              <MinusIcon className="h-5 w-5 text-orange-600" />
                                            ) : (
                                              <PlusIcon className="h-5 w-5 text-orange-600" />
                                            )}
                                          </div>

                                          <div><p className="text-[11px] font-bold text-orange-600 mx-1 px-1">{action.sponsorActionTypeId.actionTypeName}</p></div>
                                          <div><p className="text-[11px] font-bold mx-1">Action {actionIndex+1}.</p></div>
                                          <div><p className="text-[11px] text-gray-600 mx-1">{action.sponsorSessionActionName}</p></div>
                                    </div>

                                    <div className="col-span-3 flex justify-between items-center ">
                                      <div className="flex justify-start items-center">
                                        <p className="text-gray-600 text-[9px]">Complete by:</p>
                                      <CalendarDaysIcon className="m-2 h-5 w-5 text-orange-600" />
                                      <p className="flex justify-center items-center text-[9px] rounded-full px-2 py-[1px] bg-white border text-gray-600 border-orange-400">
                                        {formatDate(action?.sponsorSessionActionCompleteBy)}
                                      </p>
                                      </div>
                                      
                                      <div className="flex justify-center items-center">
                                        <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                          <PencilSquareIcon
                                            // 11 Sep 2024 - Add an Onclick function here
                                            onClick={() => handleAddOrUpdatePlannedAction(action)}
                                            className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                          />
                                        </div>
                                        {/* <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                          <TrashIcon
                                            // Add an Onclick function here
                                            className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                                          />
                                        </div> */}
                                      </div>
                                    </div>

                                  </div>
                                  {/* Show User Action Update for each planned action here*/}
                                  {/* {showSponsorUserActionUpdate[action._id] && userCount>0 && sessionUsers?.map((user, userIndex)=> {

                                  })} */}

                                  {/* Show User Action Update for each planned action here */}
                                  {showSponsorUserActionUpdate[action._id] && action.sessionUsersActions?.length > 0 && action.sessionUsersActions.map((userAction, userActionIndex) => {
                                    // Find the corresponding user for the userAction
                                    const user = sessionUsers.find(u => u.userId._id === userAction.userActionId.userId._id);
                                    if (!user) return null;

                                    // Create userInfo object
                                    const userInfo = {
                                      _id: user.userId._id,
                                      username: user.userId.username,
                                      name: user.userId.name,
                                    };

                                    return (
                                      <div key={userAction.userActionId._id + userActionIndex} className="">
                                        {/* Pass userInfo and userActionId to EtUserActionListByIdComp */}
                                        <EtUserActionListByIdComp
                                          userInfo={userInfo}
                                          userActionId={userAction.userActionId._id}
                                          userId={userInfo._id}
                                          trailId={trailId}
                                          sessionId={sessionId}
                                        />
                                      </div>
                                    );
                                  })}

                                  
                                </div>

                              </div>
                            )
                          })}
                  
                    </div>  
                  </div>
                )}
                {/* Modal for Creating Sponsor User Org Trail Session Actions */}
                <EtModalAddOrUpdateSponsorUserOrgTrailSessionAction 
                isOpen={isActionModalOpen}
                onClose={handleCloseCreateActionModal}
                sponsorId={sponsorId}
                sponsorOrgId={sponsorOrgId}
                userOrgId={sponsorOrgId}
                trailId={trailId}
                sessionId={sessionId}
                triggeringUrl={triggeringActionUrl}
                sessionUsers={filteredSessionUsers}         // 02 Sep 2024 - added to pass sessionUsers, 10 Sep 2024 - updated to filteredSessionUsers
                mode={actionMode} // 11 Sep 2024
                existingActionData={existingActionData} // 11 Sep 2024
                />
             </div>
          </div>
        )}
      </div>
    </div>
  )
}

const User = ({ username, defaultUserPic }) => {
  const { data: userPic, isFetching: isFetchingUserPic } =
    useGetUserPicQuery(username)

  return (
    <div className="flex justify-center items-center h-12 w-12 rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow-lg shadow-black cursor-pointer">
      {/* Display user picture or loading indicator*/}
      {isFetchingUserPic ? (
        <div>Loading...</div>
      ) : userPic ? (
        <img src={userPic} alt="User" className="h-11 w-11  rounded-full" />
      ) : (
        <img src={defaultUserPic} alt="User" className="h-10 w-10 p-1" />
      )}
    </div>
  )
}

// 30 Aug 2024 - Helper function to render user info and evaluation comments
const renderUserEvalInfo = (user, sponsorEvalText, evalUpdateDate, textColor = "text-gray-800") => (
  <div className="flex flex-col items-start justify-start mx-1 px-1">
      <div className="flex justify-start items-center w-full">
          <div className="flex justify-start items-center m-1 p-1 w-16">
              {/* User Info */}
              <div className="flex flex-col justify-center items-center">
                  <div className="w-16">
                      <p className="text-orange-600 text-[9px] text-center">
                          User
                      </p>
                  </div>
                  <div className="mx-1 px-1">
                      <User
                          username={user.userId?.username}
                          defaultUserPic={userProfileIcon2}
                      />
                  </div>
                  <div className="w-16 my-1 py-1">
                      <p className="text-gray-600 text-[10px] text-center">
                          {user.userId?.name.split(" ")[0]}
                      </p>
                  </div>
              </div>
          </div>
          <div className="m-1 p-1 border-l-2 border-orange-600">
              <div>
                  <p className="mx-1 text-gray-600 font-bold text-[10px]">
                      Comments
                  </p>
              </div>
              <div>
                  <p className={`mx-1 ${textColor} text-[10px]`}>
                      {sponsorEvalText}
                  </p>
              </div>
          </div>
      </div>

      <div className="m-1 p-1">
          <div className="flex justify-start items-center">
              <ClockIcon className="h-5 w-5 text-lime-600" />
              <p className="mx-1 px-1 text-gray-500 text-[10px] font-bold">
                  Last Updated
              </p>
              <p className="text-[9px] mx-1 italic px-2 py-1 text-gray-400">
                  {getTimeSince(evalUpdateDate)}
              </p>
          </div>
      </div>
  </div>
);

// 13 Sep 2024 - Component for rendering coach's notes
const RenderNotes = ({
  note,
  noteTextKey,           // e.g., "coachSharedUserNoteText", "coachAllUsersNoteText"
  updateDateKey,         // e.g., "coachSharedUserNoteUpdateDate", "coachAllUsersNoteUpdateDate"
  createdDateKey,        // e.g., "coachSharedUserNoteDate", "coachAllUsersNoteDate"
  showDetail,            // State variable controlling visibility of full note
  toggleShowDetail,      // Function to toggle visibility
  handleEditNote,        // Function to handle editing of the note
  handleDeleteNote,      // Function to handle deletion of the note
  noteIndex,
  userId,
  arr,
  createMarkup,
}) => {

  const noteText = note[noteTextKey];
  const updateDate = note[updateDateKey];
  const createdDate = note[createdDateKey];
  const noteId = note._id;

  return (
    noteText.length > 0 && (
      <div className="bg-gray-100 m-1 p-1 rounded">
        <div className="grid grid-cols-8 justify-between items-center m-1 p-1">
          <div className="col-span-1 flex justify-start items-center">
            <div
              onClick={() => toggleShowDetail(noteId)}
              className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer"
            >
              {showDetail[noteId] ? (
                <MinusIcon className="h-4 w-4 text-orange-600" />
              ) : (
                <PlusIcon className="h-4 w-4 text-orange-600" />
              )}
            </div>
            <div>
              {/* <p className="text-2xl font-bold mx-1 px-1 text-gray-600">{arr.length - noteIndex}.</p> */}
              <p className="text-2xl font-bold mx-1 px-1 text-gray-600">{ noteIndex +1}.</p>
            </div>
            <div>
              <p className="text-[9px] mx-1 rounded-full px-2 py-[1px] bg-lime-200 w-18">
                {getTimeSince(updateDate)}
              </p>
              <p className="text-[7px] mx-1 italic px-2 py-1 text-gray-400">last updated</p>
            </div>
          </div>

          <div className="col-span-4 flex justify-start items-center">
            <div
              dangerouslySetInnerHTML={createMarkup(noteText)}
              className="text-[11px] text-gray-700 m-1 p-1 truncate max-w-full line-clamp-1"
            ></div>
          </div>

          <div className="col-span-2 flex justify-start items-center mx-2">
            <ClockIcon className="h-5 w-5 text-lime-600" />
            <p className="text-[9px] mx-1">First Created:</p>
            <p className="text-gray-400 text-[9px] mx-2">{formatDateTime(createdDate)}</p>
          </div>

          <div className="col-span-1 flex justify-start items-center mx-2">
            <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
              <PencilSquareIcon
                className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                // onClick={() => handleEditNote(userId, note)}
                onClick={() => (userId ? handleEditNote(userId, note) : handleEditNote(note))}
              />
            </div>
            <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
              <TrashIcon
                className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
                onClick={() => handleDeleteNote(noteId)}
              />
            </div>
          </div>
        </div>

        {showDetail[noteId] && (
          <div className="m-1 p-1 rounded bg-gray-50">
            <div
              dangerouslySetInnerHTML={createMarkup(noteText)}
              className="fix-nested-lists text-[11px] text-gray-700 m-1 p-1"
            ></div>
          </div>
        )}
      </div>
    )
  );
}

export default EtSponsorOrgTrailSessionPadCompProd